import { Box } from "@mui/material";
import { FC, useEffect, useState } from "react";
import { ExtendedFieldError, FormQuestion } from "vmd-types";

import { ConditionCandidate, FormQuestionEditor } from "./form-question-editor";
import { BLOCK_TRANSITION_MS } from "./utils";

// Editors mounted in the first render; the rest follow once the expand settles.
const FIRST_MOUNT_COUNT = 10;

export interface BlockQuestionListProps {
  questions: FormQuestion[];
  formIndex: number;
  blockIndex: number;
  allQuestions: ConditionCandidate[];
  validationErrors: Record<string, ExtendedFieldError>;
  language: string;
  onChange: (index: number, question: FormQuestion) => void;
  onDelete: (index: number) => void;
  onMove: (index: number, direction: -1 | 1) => void;
}

/**
 * Renders a block's question editors, mounting them in two passes.
 *
 * A block can hold ~60 questions and each one mounts a select over every
 * question in the blueprint, which is enough work to stall the accordion's
 * expand animation. Lives in its own component so the accordion's unmountOnExit
 * resets the mount count on collapse without an effect.
 */
export const BlockQuestionList: FC<BlockQuestionListProps> = ({
  questions,
  formIndex,
  blockIndex,
  allQuestions,
  validationErrors,
  language,
  onChange,
  onDelete,
  onMove,
}) => {
  const [mountLimit, setMountLimit] = useState(FIRST_MOUNT_COUNT);

  useEffect(() => {
    if (questions.length <= FIRST_MOUNT_COUNT) return undefined;
    const handle = window.setTimeout(
      () => setMountLimit(questions.length),
      BLOCK_TRANSITION_MS
    );
    return () => window.clearTimeout(handle);
  }, [questions.length]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {questions.slice(0, mountLimit).map((question, questionIndex) => (
        <FormQuestionEditor
          key={question.id}
          question={question}
          formIndex={formIndex}
          blockIndex={blockIndex}
          questionIndex={questionIndex}
          isFirst={questionIndex === 0}
          isLast={questionIndex === questions.length - 1}
          allQuestions={allQuestions}
          validationErrors={validationErrors}
          language={language}
          onChange={onChange}
          onDelete={onDelete}
          onMove={onMove}
        />
      ))}
    </Box>
  );
};
